import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { MassChannel } from '#/shared/api/generated/communication/models'
import type {
  MassRecipientInput,
  MassSendPreview,
} from '#/shared/api/generated/communication/models'
import {
  previewMassEmailApiV1SendMassEmailPreviewPost,
  previewMassSmsApiV1SendMassSmsPreviewPost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { useDebouncedValue } from '#/shared/lib/hooks'
import {
  EMPTY_SMS_DELIVERABILITY,
  toSmsDeliverability,
} from '#/shared/lib/sms-preview'

import { computeMassPreview } from './mass-preview'
import type { MassPreviewResult } from './mass-preview'

/** The composer keystroke settles before the preview round trip fires. */
const PREVIEW_DEBOUNCE_MS = 400

function toPreviewResult(preview: MassSendPreview): MassPreviewResult {
  return {
    totalSelected: preview.total_selected,
    recipientCount: preview.kept_count,
    removedCount: preview.removed_count,
    removed: (preview.removed ?? []).map((recipient, index) => ({
      // Server collapses by (contact, reason); index keeps the key unique even
      // when the same contact appears under two different reasons.
      id: `${recipient.contact}-${index}`,
      name: recipient.name ?? '',
      contact: recipient.contact,
      reason: recipient.removed_reason,
      count: recipient.count ?? 1,
    })),
    // `can_send` also goes false on an empty recipient list, which the composers
    // already gate on — only a blast-level blocker (quota) reads as blocked
    // here, and `toSmsDeliverability` requires one to say so.
    deliverability: toSmsDeliverability(preview),
  }
}

/** The email preview has no body lint — its result carries no findings. */
function toEmailPreviewResult(preview: MassSendPreview): MassPreviewResult {
  return {
    ...toPreviewResult(preview),
    deliverability: EMPTY_SMS_DELIVERABILITY,
  }
}

interface MassSmsPreviewOptions {
  /** Plain-text blast body — sent so the server lints it for the signals that
   *  get a message carrier-spam-blocked. Omit it and only the counts come back. */
  message?: string
  /** Promotional traffic is held to the stricter opt-out check. */
  isMarketing?: boolean
}

/**
 * Server-side curation for the mass SMS composer: the preview endpoint runs the
 * same dedup + invalid + consent gate as send (do_not_text is scoped to the
 * selected sender line, which the client can't know) and lints the body the
 * blast would carry, without persisting a job. `computeMassPreview` fills the
 * counts instantly and stays as the fallback while the round trip is in flight
 * or if it fails — it carries no findings, since only the server lints.
 */
export function useMassSmsPreview(
  recipients: MassRecipientInput[],
  userPhoneId: string,
  { message = '', isMarketing = false }: MassSmsPreviewOptions = {},
): MassPreviewResult {
  const local = useMemo(
    () => computeMassPreview(MassChannel.sms, recipients),
    [recipients],
  )
  const debouncedMessage = useDebouncedValue(message, PREVIEW_DEBOUNCE_MS)
  const { data } = useQuery({
    queryKey: [
      'mass-preview',
      MassChannel.sms,
      userPhoneId,
      recipients,
      debouncedMessage,
      isMarketing,
    ],
    queryFn: async ({ signal }) => {
      const response = await previewMassSmsApiV1SendMassSmsPreviewPost(
        {
          user_phone_id: userPhoneId,
          recipients,
          message: debouncedMessage || null,
          is_marketing: isMarketing,
        },
        signal,
      )
      return response.data
    },
    select: toPreviewResult,
    placeholderData: keepPreviousData,
    enabled: recipients.length > 0 && Boolean(userPhoneId),
  })
  const result = data ?? local
  // Findings describe the linted body. An empty one comes back with only
  // "empty after cleaning", which is noise before anything is typed — and gating
  // on the *debounced* body (what the server actually saw) rather than the live
  // one keeps a stale finding from flashing on the first keystroke. Recipient
  // curation (counts/removals) is independent of the body, so it stays.
  if (debouncedMessage.trim()) return result
  return {
    ...result,
    deliverability: { ...result.deliverability, findings: [] },
  }
}

/** Same as `useMassSmsPreview` for the mass email composer — the server also
 *  drops unsubscribed addresses (email opt-out list). */
export function useMassEmailPreview(
  recipients: MassRecipientInput[],
): MassPreviewResult {
  const local = useMemo(
    () => computeMassPreview(MassChannel.email, recipients),
    [recipients],
  )
  const { data } = useQuery({
    queryKey: ['mass-preview', MassChannel.email, recipients],
    queryFn: async ({ signal }) => {
      const response = await previewMassEmailApiV1SendMassEmailPreviewPost(
        { recipients },
        signal,
      )
      return response.data
    },
    select: toEmailPreviewResult,
    placeholderData: keepPreviousData,
    enabled: recipients.length > 0,
  })
  return data ?? local
}
